import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Search } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { useDataLoader } from "../hooks/useDataLoader";
import { loadUSSDCodes, searchData } from "../utils/dataLoader";
import type { USSDCode } from "../types/data";
import { SEO } from "../components/SEO";
import { routeSeo } from "../content/seoRoutes";

export function UssdCodesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearchTerm = searchParams.get('q') || "";

  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearchTerm);

  // Load USSD codes data
  const { data: ussdCodes, loading, error } = useDataLoader<USSDCode[]>(loadUSSDCodes, []);

  // Debounce search term to avoid lagging while typing
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const trimmed = searchTerm.trim();
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        return next;
      }, { replace: true });
    }, 200);
    return () => clearTimeout(id);
  }, [searchTerm, setSearchParams]);

  // Sync URL search parameters to state on mount/back navigation
  useEffect(() => {
    setSearchTerm(urlSearchTerm);
    setDebouncedSearch(urlSearchTerm);
  }, [urlSearchTerm]);

  const filteredUssdCodes = useMemo(() => {
    if (!ussdCodes) return [] as USSDCode[];
    if (!debouncedSearch) return ussdCodes;

    return searchData<USSDCode>(ussdCodes, debouncedSearch, [
      "code",
      "operator",
      "description",
      "category",
    ]);
  }, [ussdCodes, debouncedSearch]);

  // Group by operator and then by category
  const groupedCodes = useMemo(() => {
    const grouped: { 
      [operator: string]: { 
        [category: string]: USSDCode[] 
      } 
    } = {};

    filteredUssdCodes.forEach((code: USSDCode) => {
      if (!grouped[code.operator]) {
        grouped[code.operator] = {};
      }
      if (!grouped[code.operator][code.category]) {
        grouped[code.operator][code.category] = [];
      }
      grouped[code.operator][code.category].push(code);
    });
    return grouped;
  }, [filteredUssdCodes]);

  const SearchInput = ({ value, onChange, placeholder }: { 
    value: string; 
    onChange: (value: string) => void; 
    placeholder: string 
  }) => (
    <div className="relative mb-6 max-w-2xl mx-auto">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
      <Input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-10 pr-4 py-6 text-base"
      />
    </div>
  );

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "Indian Telecom USSD Codes Database",
    "description": "Complete database of USSD (Unstructured Supplementary Service Data) codes for Indian telecom operators including balance check, recharge, and caller tunes",
    "keywords": ["USSD codes", "telecom codes", "mobile shortcodes", "operator shortcuts", "Indian telecom codes"],
    "license": "https://creativecommons.org/licenses/by/4.0/",
    "creator": {
      "@type": "Organization",
      "name": "Indian Codes Hub"
    },
    "distribution": {
      "@type": "DataDownload",
      "contentUrl": `${window.location.origin}/ussd-codes`,
      "encodingFormat": "text/html"
    }
  };

  return (
    <>
      <SEO
        {...routeSeo('ussd-codes')}
        keywords="USSD codes, telecom codes, mobile shortcodes, operator shortcuts, balance check codes, Airtel USSD, Jio USSD, Vi USSD"
        structuredData={structuredData}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">USSD Codes</h1>
        <p className="text-sm sm:text-base text-gray-600">Search for telecom USSD codes by operator, service, or category</p>
      </div>

      <SearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search by code, operator, service, or category..."
      />

      {loading && (
        <div className="text-center py-12 text-gray-500">Loading USSD codes...</div>
      )}
      {error && (
        <div className="text-center py-12 text-red-500">{error}</div>
      )}

      <div className="space-y-12">
        {Object.entries(groupedCodes).map(([operator, categories]) => (
          <div key={operator} className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">
              {operator}
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({Object.values(categories).flat().length} codes)
              </span>
            </h2>

            {Object.entries(categories).map(([category, codes]) => (
              <div key={`${operator}-${category}`} className="mb-8">
                <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                  <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  {category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {codes.map((code, index) => (
                    <Card key={`${code.code}-${index}`} className="hover:shadow-lg transition-shadow h-full">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex flex-col space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xl font-bold text-blue-600">{code.code}</span>
                            <Badge variant="outline" className="text-xs">
                              {code.category}
                            </Badge>
                          </div>
                          {code.operator && (
                            <span className="text-sm text-gray-500 font-normal">
                              {code.operator}
                            </span>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600">{code.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {!loading && !error && ussdCodes && filteredUssdCodes.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No USSD codes found matching your search.</p>
        </div>
      )}
    </div>
    </>
  );
}